// Which handset, and which port on it, this line's phone registers as.
//
// Verso-only: nothing is sent to the phone here. The GDMS push that actually tells the
// handset what to load happens when the line is built — so this is the decision, and
// provisioning is the delivery. That order is the whole reason the extension is created
// when a line is sold: before that there was nowhere to record the choice, because the
// binding hangs off the extension.
//
// Port numbers are not validated against the model on purpose. GDMS owns the real binding
// and Verso adopts it (project_gdms_binding_authority), and a DP750's capacity was wrong in
// our code for months because the number was hand-kept while GDMS reported something else.
// So an operator can pick a port we have never seen bound, and the server creates the row
// on demand.
//
// "Port" throughout, not "slot" — it is what the hardware and every other screen call it
// (an ATA has FXS ports; GDMS labels a DECT base's accounts by port). The mixed vocabulary
// was mine and it made two names for one thing.

import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api.js';

/**
 * How a device reads in a picker.
 *
 * A CALIX LEADS WITH ITS FSAN. That is the identifier printed on the unit and the key Calix
 * Cloud uses; its MAC means nothing to whoever is standing in front of the router. Reported
 * 2026-07-29 when assigning CXNK010F04DB proved impossible because the picker labelled on
 * MAC/model only, and it listed as B89470454CB0.
 *
 * The fix lived in the device picker inside the customer page's "account-wide" Extensions
 * section. That section was deleted on 2026-08-13 and binding moved here — so without this
 * the fix would have been quietly undone by a refactor, which is the way capabilities
 * actually get lost.
 */
export function deviceLabel(d) {
  const isCalix = String(d?.deviceType || d?.device_type || '').toLowerCase() === 'calix'
    || Boolean(d?.fsan);
  const id = isCalix ? (d?.fsan || d?.mac) : d?.mac;
  const name = d?.name || d?.model || id;
  return `${name}${id ? ` · ${id}` : ''}${isCalix ? ' · Calix' : ''}`;
}


// `extension` binds ONE extension; without it the dialog binds the line's own account, which
// is what it has always done. Two entry points, one form — a line ringing five extensions has
// five devices, and routing them all through the line's account is what made a device chosen
// from one row land on all five (Kevin, live, 2026-08-11).
export default function SetDevicePortDialog({ line, extension, subscriberId, onClose, onSaved }) {
  const target = extension || line;
  const [mac, setMac] = useState(target.deviceMac || '');
  const [port, setPort] = useState(String(target.devicePort ?? 1));

  // Only this customer's hardware. Binding somebody else's handset would have their
  // phone ring for this line, and the server refuses it — but a dropdown that offers the
  // impossible is a worse way to learn that than not offering it.
  // `GET /devices?subscriberId=` — the endpoint that EXISTS. This was written against
  // `/subscribers/:id/devices`, which is not a route: the request 404'd, React Query
  // retried it, and the dialog sat on "Loading this customer's devices…" while showing
  // "No device" for a line that had one. Found live 2026-08-04 on +17208644320, and it
  // meant no handset could be bound or changed from here at all.
  const devices = useQuery({
    queryKey: ['devices', { subscriberId }],
    queryFn: () => api.get('/devices', { subscriberId }),
    enabled: Boolean(subscriberId),
  });
  const list = Array.isArray(devices.data) ? devices.data : (devices.data?.devices || []);

  // The currently-bound handset, kept selectable even before the list arrives. Without it
  // the <select> falls back to its first option — "No device" — so a line WITH a handset
  // renders as unbound for as long as the fetch takes, which is exactly how this looked
  // broken. `mac` state is still correct underneath, but a control that displays the wrong
  // current value is a control an operator cannot trust.
  const known = new Set(list.map((d) => d.mac).filter(Boolean));
  const boundMissing = target.deviceMac && !known.has(target.deviceMac);

  const save = useMutation({
    mutationFn: () => api.put(
      extension
        ? `/sip-accounts/${extension.sipAccountId}/device-port`
        : `/lines/${line.id}/device-port`,
      mac ? { mac, port: Number(port) } : { mac: null },
    ),
    onSuccess: (l) => onSaved?.(l),
  });

  const unbinding = !mac;

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-md rounded-lg bg-white shadow-xl">
        <div className="border-b border-gray-200 px-5 py-4">
          <h2 className="text-base font-semibold text-gray-900">
            {extension
              ? `Device for extension ${extension.username}`
              : `Device for ${line.number || line.label || `line ${line.id}`}`}
          </h2>
          <p className="mt-1 text-sm text-gray-600">
            Recorded in Verso now; the phone is told when the line is built.
          </p>
        </div>

        <div className="space-y-4 px-5 py-4">
          <div>
            <label htmlFor="device-pick" className="block text-sm font-medium text-gray-700">
              Device
            </label>
            <select
              id="device-pick"
              value={mac}
              onChange={(e) => setMac(e.target.value)}
              className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
            >
              <option value="">No phone (softphone, or not decided)</option>
              {/* First, and only while the list has not caught up: the device this line
                  already has. Otherwise the select shows "No device" for a bound line. */}
              {boundMissing && (
                <option value={line.deviceMac}>
                  {line.deviceName || line.deviceModel || line.deviceMac} · {line.deviceMac}
                  {devices.isLoading ? ' (current)' : ' (not in this customer’s devices)'}
                </option>
              )}
              {list.map((d) => (
                <option key={d.mac || d.id} value={d.mac || ''}>
                  {deviceLabel(d)}
                </option>
              ))}
            </select>
            {devices.isLoading && <p className="mt-1 text-xs text-gray-500">Loading this customer’s devices…</p>}
            {devices.error && (
              // "Couldn't ask" is not "there are none" — the difference between a broken
              // dialog and a customer with no hardware.
              <p className="mt-1 text-xs text-red-700">
                Couldn’t load this customer’s devices: {devices.error.body?.error || devices.error.message}
              </p>
            )}
            {!devices.isLoading && !devices.error && !list.length && (
              <p className="mt-1 text-xs text-amber-700">
                This customer has no devices assigned. Assign one on the Devices tab first.
              </p>
            )}
          </div>

          {!unbinding && (
            <div>
              <label htmlFor="device-port" className="block text-sm font-medium text-gray-700">
                Port
              </label>
              <input
                id="device-port"
                type="number"
                min="1"
                value={port}
                onChange={(e) => setPort(e.target.value)}
                className="mt-1 w-24 rounded-md border border-gray-300 px-3 py-2 text-sm"
              />
              <p className="mt-1 text-xs text-gray-500">
                Account 1 on a desk phone, the FXS port on an ATA, or the account a DECT
                base’s handsets are pointed at. One line per port.
              </p>
            </div>
          )}

          {unbinding && line.deviceMac && (
            <p className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
              This releases {line.deviceMac} from this line in Verso. The phone keeps its
              current config until the line is built or taken down.
            </p>
          )}

          {save.error && (
            <p className="text-sm text-red-700">
              {save.error.body?.error || save.error.message}
            </p>
          )}
        </div>

        <div className="flex justify-end gap-2 border-t border-gray-200 px-5 py-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={save.isPending || (!unbinding && !(Number(port) >= 1))}
            onClick={() => save.mutate()}
            className="rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {save.isPending ? 'Saving…' : unbinding ? 'Release phone' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
